import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  Building2, 
  MapPin, 
  Calendar, 
  Award,
  Search,
  ShieldCheck,
  Loader2,
  Globe
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MembersPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await axios.get(`${API}/members/public`);
        setMembers(response.data);
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);

  const filteredMembers = members.filter(member => {
    const matchesSearch = member.member_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.country?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || member.membership_type === filterType;
    return matchesSearch && matchesType;
  });

  const getMembershipBadge = (type) => {
    const styles = {
      founding: 'bg-[#F5A623] text-[#0E285B]',
      full: 'bg-[#0E285B] text-white',
      associate: 'bg-slate-600 text-white',
      honorary: 'bg-purple-600 text-white'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${styles[type] || styles.full}`}>
        {type} Member
      </span>
    );
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#0E285B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Users className="h-6 w-6 text-[#F5A623]" />
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                Our Network
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Members Directory
            </h1>
            <p className="text-slate-300 text-lg">
              Explore our global network of educational institutions and organizations 
              committed to advancing international education.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-8 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold text-[#0E285B]">{members.length}</p>
              <p className="text-sm text-slate-500">Total Members</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#F5A623]">
                {members.filter(m => m.membership_type === 'founding').length || 10}
              </p>
              <p className="text-sm text-slate-500">Founding Members</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0E285B]">
                {[...new Set(members.map(m => m.country))].length || 1}
              </p>
              <p className="text-sm text-slate-500">Countries</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0E285B]">
                {members.filter(m => m.membership_type === 'full').length}
              </p>
              <p className="text-sm text-slate-500">Full Members</p>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by name or country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-slate-200"
                data-testid="member-search-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'founding', 'full', 'associate', 'honorary'].map((type) => (
                <Button
                  key={type}
                  variant={filterType === type ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterType(type)}
                  className={filterType === type 
                    ? 'bg-[#0E285B] text-white' 
                    : 'border-slate-300 text-slate-600'
                  }
                  data-testid={`filter-${type}`}
                >
                  {type === 'all' ? 'All Members' : `${type.charAt(0).toUpperCase() + type.slice(1)}`}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Members Grid */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-[#F5A623]" />
            </div>
          ) : filteredMembers.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMembers.map((member, index) => (
                <div 
                  key={member.certificate_number || index}
                  className="bg-white p-6 rounded-sm shadow-sm hover:shadow-md transition-all border border-slate-100 group"
                  data-testid={`member-card-${index}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-14 w-14 bg-gradient-to-br from-[#0E285B] to-[#1a3a7a] rounded-full flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-7 w-7 text-[#F5A623]" />
                    </div>
                    {getMembershipBadge(member.membership_type)}
                  </div>
                  
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#0E285B] mb-3 group-hover:text-[#F5A623] transition-colors">
                    {member.member_name}
                  </h3>
                  
                  <div className="space-y-2 text-sm text-slate-600">
                    {member.country && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-[#F5A623]" />
                        <span>{member.country}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-[#F5A623]" />
                      <span>Member since {member.issue_date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-[#F5A623]" />
                      <span className="font-mono text-xs">{member.certificate_number}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <Link 
                      to={`/verify/${member.certificate_number}`}
                      className="inline-flex items-center gap-2 text-sm text-[#0E285B] hover:text-[#F5A623] transition-colors"
                    >
                      <ShieldCheck className="h-4 w-4" />
                      Verify Certificate
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Globe className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0E285B] mb-2">No Members Found</h3>
              <p className="text-slate-500">
                {searchTerm || filterType !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Members will appear here once they join UIEHEC'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0E285B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-white mb-4">
            Join Our Global Network
          </h2>
          <p className="text-slate-300 mb-8">
            Become part of a dynamic community of educational institutions committed to 
            excellence in international education.
          </p>
          <Link to="/apply">
            <Button className="bg-[#F5A623] text-[#0E285B] hover:bg-[#F5A623]/90 h-12 px-8 font-semibold">
              Apply for Membership
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
